import React, { useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { borderRadius } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useLocale } from '@atlassian/jira-tenant-context-controller';
import { cardBoxShadow, cardBoxHoverShadow } from '../../../../../../common/constants';
import { useCodeLocalStore } from '../../../../../../controllers';
import messages from './messages';
import type { Props } from './types';
import { getLastUpdateText } from './utils';

export const RepoCard = ({
	repoName,
	providerName,
	providerId,
	repoUrl,
	lastUpdated,
	iconUrl,
	type,
}: Props) => {
	const locale: Locale = useLocale();
	const { formatMessage } = useIntl();
	const dateString = getLastUpdateText(lastUpdated, locale);
	const [codeLocalState] = useCodeLocalStore();
	const repoProviderName = providerName;

	const onClick = useCallback(
		(_: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'codeRepositoryTile', {
				nextgen: codeLocalState.projectContext.simplified,
				projectId: codeLocalState.projectContext.projectId,
				...(type && { providerType: type }),
				...(providerId && { providerId }),
				repoUrl,
			});
		},
		[
			codeLocalState.projectContext.simplified,
			codeLocalState.projectContext.projectId,
			repoUrl,
			type,
			providerId,
		],
	);

	return (
		<RepositoryCardWrapperLink
			href={repoUrl}
			onClick={onClick}
			target="_blank"
			data-testid="software-code-page.ui.pages.main-page.repositories.repository-list.repository-card.repository-card-wrapper-link"
		>
			<RepositoryCardContainer>
				<Box xcss={repositoryLinkContentStyles}>
					<RepositoryProviderTypeContainer>
						<Tooltip content={repoProviderName}>
							<RepositoryProviderTypeName>{repoProviderName}</RepositoryProviderTypeName>
						</Tooltip>
					</RepositoryProviderTypeContainer>
					<Avatar src={iconUrl} appearance="square" size="medium" />
					<RepositoryDataContainer>
						<Tooltip content={repoName}>
							<Heading size="xsmall" as="div">
								<ExtendedRepositoryName>{repoName}</ExtendedRepositoryName>
							</Heading>
						</Tooltip>
					</RepositoryDataContainer>
					<RepositoryDescription>
						{formatMessage(messages.lastUpdated, { dateString })}
					</RepositoryDescription>
				</Box>
			</RepositoryCardContainer>
		</RepositoryCardWrapperLink>
	);
};
RepoCard.displayName = 'RepoCard';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExtendedRepositoryName = styled2.span({
	overflow: 'hidden',
	maxWidth: 'inherit',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	display: 'inline-block',
	outline: 'none',
	position: 'static',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryDescription = styled2.small({
	margin: 0,
	display: 'inline-block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryProviderTypeContainer = styled2.div({
	float: 'right',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.disabled', colors.N60),
	display: 'flex',
	marginTop: token('space.negative.100', '-8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryProviderTypeName = styled2.span({
	alignSelf: 'center',
	margin: 0,
	display: 'inline-block',
	overflow: 'hidden',
	maxWidth: '102px',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryCardContainer = styled2.div({
	display: 'block',
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	minWidth: 0,
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.raised', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	boxShadow: token('elevation.shadow.raised', cardBoxShadow),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	marginRight: token('space.100', '8px'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		boxShadow: token('elevation.shadow.overlay', cardBoxHoverShadow),
		transition: 'box-shadow 0.2s ease',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		position: 'relative',
		zIndex: 1,
	},
});

const repositoryLinkContentStyles = xcss({
	padding: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryDataContainer = styled2.div({
	display: 'flex',
	height: '32px',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		maxWidth: '180px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryCardWrapperLink = styled2(Link)({
	textDecoration: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	display: 'block',
	height: '100%',
	'&:hover, &:visited, &:focus, &:active': {
		outline: 'none',
		textDecoration: 'none',
	},
});
